import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp, Phone, MessageCircle } from 'lucide-react';

const FloatingCTA = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isFormFocused, setIsFormFocused] = useState(false);

  // Check if device is mobile
  const checkMobile = useCallback(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  const handleScroll = useCallback(() => {
    const scrollPosition = window.scrollY;
    const windowHeight = window.innerHeight;
    
    // Show CTA after scrolling 50% of the page, but hide if form is focused on mobile
    if (scrollPosition > windowHeight * 0.5 && !(isMobile && isFormFocused)) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [isMobile, isFormFocused]);

  // Handle form focus events
  const handleFormFocus = useCallback(() => {
    if (isMobile) {
      setIsFormFocused(true);
      setIsVisible(false);
    }
  }, [isMobile]);

  const handleFormBlur = useCallback(() => {
    if (isMobile) {
      setTimeout(() => setIsFormFocused(false), 300); // Delay to allow for form interaction
    }
  }, [isMobile]);

  useEffect(() => {
    // Initial check
    checkMobile();
    
    // Add event listeners
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkMobile);
    };
  }, [checkMobile, handleScroll]);

  useEffect(() => {
    // Add form focus listeners for mobile
    if (isMobile) {
      const formInputs = document.querySelectorAll('#lead-form input, #lead-form textarea');
      formInputs.forEach(input => {
        input.addEventListener('focus', handleFormFocus);
        input.addEventListener('blur', handleFormBlur);
      });
      
      return () => {
        // Remove form focus listeners
        formInputs.forEach(input => {
          input.removeEventListener('focus', handleFormFocus);
          input.removeEventListener('blur', handleFormBlur);
        });
      };
    }
  }, [isMobile, handleFormFocus, handleFormBlur]);

  const phoneNumber = '+91 7892064440';
  const whatsappNumber = '917892064440';
  const whatsappMessage = 'Hi! I\'m interested in your e-commerce store development services. Can you help me get started?';

  const handleScrollToForm = () => {
    // Push GTM event for form scroll action
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'cta_form_scroll',
      ctaType: 'floating_widget',
      action: 'scroll_to_form'
    });
    
    document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCall = () => {
    // Push GTM event for call action
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'cta_call_click',
      ctaType: 'floating_widget',
      action: 'call_button',
      phoneNumber: phoneNumber
    });
    
    // Fire Google Ads conversion and then proceed
    if (typeof window.gtag_report_conversion === 'function') {
      window.gtag_report_conversion(`tel:${phoneNumber}`);
    } else {
      window.open(`tel:${phoneNumber}`, '_self');
    }
  };

  const handleWhatsApp = () => {
    // Push GTM event for WhatsApp action
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'cta_whatsapp_click',
      ctaType: 'floating_widget',
      action: 'whatsapp_button',
      whatsappNumber: whatsappNumber
    });
    
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

    // Fire Google Ads conversion and then open WhatsApp
    if (typeof window.gtag_report_conversion === 'function') {
      window.gtag_report_conversion(url);
    } else {
      window.open(url, '_blank');
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 100, scale: 0.8 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          style={{
            position: 'fixed',
            bottom: isMobile ? '10px' : '20px',
            right: isMobile ? '10px' : '20px',
            left: isMobile ? '10px' : 'auto',
            zIndex: 1000,
            maxWidth: isMobile ? 'calc(100vw - 20px)' : 'none'
          }}
        >
          {/* Widget Container */}
          <motion.div
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: isMobile ? '16px' : '20px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              padding: isMobile ? '16px' : '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? '8px' : '12px',
              minWidth: isMobile ? 'auto' : '200px',
              width: isMobile ? '100%' : 'auto',
              position: 'relative'
            }}
          >
            {/* Widget Header */}
            <div style={{
              textAlign: 'center',
              marginBottom: isMobile ? '4px' : '8px',
              paddingBottom: isMobile ? '8px' : '12px',
              borderBottom: '2px solid #f1f5f9'
            }}>
              <h4 style={{
                fontSize: isMobile ? '12px' : '14px',
                fontWeight: '700',
                color: '#1e293b',
                margin: '0 0 4px 0',
                lineHeight: '1.2'
              }}>
                Get Started
              </h4>
              <p style={{
                fontSize: isMobile ? '10px' : '12px',
                color: '#64748b',
                margin: 0,
                lineHeight: '1.4'
              }}>
                Choose your preferred way
              </p>
            </div>
            {/* Call Button */}
            <motion.button
              onClick={handleCall}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white',
                border: 'none',
                borderRadius: isMobile ? '10px' : '12px',
                boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                padding: isMobile ? '10px 12px' : '12px 16px',
                fontSize: isMobile ? '12px' : '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '6px' : '8px',
                transition: 'all 0.3s ease',
                width: '100%'
              }}
            >
              <Phone size={isMobile ? 14 : 16} />
              {isMobile ? 'Call' : `Call ${phoneNumber}`}
            </motion.button>

            {/* WhatsApp Button */}
            <motion.button
              onClick={handleWhatsApp}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{
                background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                color: 'white',
                border: 'none',
                borderRadius: isMobile ? '10px' : '12px',
                boxShadow: '0 4px 15px rgba(37, 211, 102, 0.3)',
                padding: isMobile ? '10px 12px' : '12px 16px',
                fontSize: isMobile ? '12px' : '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '6px' : '8px',
                transition: 'all 0.3s ease',
                width: '100%'
              }}
            >
              <MessageCircle size={isMobile ? 14 : 16} />
              WhatsApp
            </motion.button>

            {/* Form Button */}
            <motion.button
              onClick={handleScrollToForm}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                background: 'white',
                borderRadius: isMobile ? '10px' : '12px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                border: '2px solid #e2e8f0',
                padding: isMobile ? '10px 12px' : '12px 16px',
                fontSize: isMobile ? '12px' : '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '6px' : '8px',
                color: '#667eea',
                transition: 'all 0.3s ease',
                width: '100%'
              }}
            >
              {isMobile ? 'Fill form' : 'Or fill detailed form'}
              <ArrowUp size={isMobile ? 14 : 16} />
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingCTA;
